/**
 * Fetched Content Registry for Analytics
 *
 * Stores all content (articles and videos) from API responses for use in NTP analytics events.
 * Unlike useVisibleContent which tracks only visible tiles, this module
 * stores ALL content returned by the API, regardless of viewport visibility.
 *
 * Used to populate the `visibleContent` array in:
 * - NtpPage_Viewed: includes all content from initial page load
 * - NtpMoreNews_Loaded: includes all content from that specific batch
 *
 * NOTE: The property is still called "visibleContent" for backwards compatibility
 * with existing analytics pipelines, but it now represents all fetched content,
 * not just those visible in the viewport.
 */

import { ref } from 'vue'
import debug from '../../utils/console'
import type { NewsArticle } from '../../server/models/news/news-article'
import type { NewsVideo } from '../../server/models/news/news-video'
import type { Game } from '../../server/models/game/game'
import { calculateStoryAge } from '../date/calculateStoryAge'
import { randomSeed } from '../../utils/array/seededShuffle'

const logger = debug.withTag('fetchedContent')

/**
 * Mapped content format for analytics events.
 * This matches the VisibleContent interface in types.ts for compatibility.
 */
export interface FetchedContentPayload {
  id: string
  sortOrder: number
  headline?: string
  publisher?: string
  url?: string
  summary_url?: string
  category?: string
  type?: 'article' | 'video' | 'game'
  story_age?: number
  recommendation_source?: string
}

interface ContentBatch {
  items: FetchedContentPayload[]
  timestamp: number
}

export const initialBatchAvailable = ref(false)

// Store for initial page load content (articles + videos)
let initialBatch: ContentBatch | null = null

// Store for the most recent "more news" batch
let latestMoreNewsBatch: ContentBatch | null = null

// Running count of total content items fetched (for sortOrder across batches)
let totalContentFetched = 0

// Persistent index mapping content ID → sortOrder across ALL batches.
// Unlike initialBatch/latestMoreNewsBatch which only keep the most recent batches,
// this index retains sort orders from all batches ever registered (including middle
// more-news batches that get replaced in latestMoreNewsBatch).
const sortOrderIndex = new Map<string, number>()

// Per-pageview seed for the first-load randomization test (SUP-504). Null until
// randomization is applied, so its presence signals the randomized bucket.
let pageviewSeed: number | null = null

export function ensurePageviewSeed(): number {
  return (pageviewSeed ??= randomSeed())
}

export function getPageviewSeed(): number | null {
  return pageviewSeed
}

/**
 * Map a NewsArticle to the analytics payload format.
 * @param article - The NewsArticle from the API response
 * @param index - The index within the current batch (used for sortOrder within batch)
 * @param globalOffset - Offset to add for global sortOrder (total content before this batch)
 * @param summaryUrlBuilder - Optional function to build the OneNews summary page URL
 */
export function mapArticleToPayload(
  article: NewsArticle,
  index: number,
  globalOffset: number = 0,
  summaryUrlBuilder?: (article: NewsArticle) => string
): FetchedContentPayload {
  return {
    id: article.id,
    sortOrder: globalOffset + index,
    headline: article.title,
    publisher: article.nameSource,
    url: article.link || undefined,
    summary_url: summaryUrlBuilder ? summaryUrlBuilder(article) : undefined,
    category: article.category,
    type: 'article',
    story_age: article.publishedDate
      ? calculateStoryAge(article.publishedDate)
      : undefined,
    recommendation_source: article.recommendationSource,
  }
}

/**
 * Map a NewsVideo to the analytics payload format.
 * @param video - The NewsVideo from the API response
 * @param index - The index within the current batch (used for sortOrder within batch)
 * @param globalOffset - Offset to add for global sortOrder (total content before this batch)
 * @param summaryUrlBuilder - Optional function to build the OneNews summary page URL
 */
export function mapVideoToPayload(
  video: NewsVideo,
  index: number,
  globalOffset: number = 0,
  summaryUrlBuilder?: (video: NewsVideo) => string
): FetchedContentPayload {
  return {
    id: video.id,
    sortOrder: globalOffset + index,
    headline: video.title,
    publisher: undefined, // Videos don't have nameSource
    url: video.link || undefined,
    summary_url: summaryUrlBuilder ? summaryUrlBuilder(video) : undefined,
    category: video.category,
    type: 'video',
    story_age: video.publishedDate
      ? calculateStoryAge(video.publishedDate)
      : undefined,
  }
}

/**
 * Add content items to a batch (either initial or more-news).
 * This is an internal helper that handles the accumulation logic.
 */
function addToBatch(
  items: FetchedContentPayload[],
  isInitialBatch: boolean
): void {
  // Populate persistent sort order index for all items.
  // Only store the first occurrence — the API can return duplicate IDs across
  // batches, and the sort order from the earliest batch is the one that matches
  // the visibleContent array already sent in the corresponding analytics event.
  for (const item of items) {
    if (!sortOrderIndex.has(item.id)) {
      sortOrderIndex.set(item.id, item.sortOrder)
    }
  }

  if (isInitialBatch) {
    if (!initialBatch) {
      initialBatch = { items: [], timestamp: Date.now() }
    }
    initialBatch.items.push(...items)
    initialBatch.timestamp = Date.now()
    initialBatchAvailable.value = true
  } else {
    if (!latestMoreNewsBatch) {
      latestMoreNewsBatch = { items: [], timestamp: Date.now() }
    }
    latestMoreNewsBatch.items.push(...items)
    latestMoreNewsBatch.timestamp = Date.now()
  }
}

/**
 * Register articles from an API response as a batch.
 * @param articles - Array of NewsArticle from the API response
 * @param isInitialBatch - True for the first page load, false for "more news" loads
 * @param summaryUrlBuilder - Optional function to build OneNews summary page URLs
 */
export function registerArticleBatch(
  articles: NewsArticle[],
  isInitialBatch: boolean,
  summaryUrlBuilder?: (article: NewsArticle) => string
): void {
  if (!articles || articles.length === 0) {
    logger.warn('registerArticleBatch called with empty articles array')
    return
  }

  const globalOffset = totalContentFetched
  const mappedArticles = articles.map((article, index) =>
    mapArticleToPayload(article, index, globalOffset, summaryUrlBuilder)
  )

  addToBatch(mappedArticles, isInitialBatch)
  totalContentFetched += articles.length

  logger.info(
    `Registered ${isInitialBatch ? 'initial' : 'more-news'} batch with ${articles.length} articles for analytics (total: ${totalContentFetched})`
  )
}

/**
 * Register videos from an API response as a batch.
 * @param videos - Array of NewsVideo from the API response
 * @param isInitialBatch - True for the first page load, false for "more news" loads
 * @param summaryUrlBuilder - Optional function to build OneNews summary page URLs
 */
export function registerVideoBatch(
  videos: NewsVideo[],
  isInitialBatch: boolean,
  summaryUrlBuilder?: (video: NewsVideo) => string
): void {
  if (!videos || videos.length === 0) {
    logger.warn('registerVideoBatch called with empty videos array')
    return
  }

  const globalOffset = totalContentFetched
  const mappedVideos = videos.map((video, index) =>
    mapVideoToPayload(video, index, globalOffset, summaryUrlBuilder)
  )

  addToBatch(mappedVideos, isInitialBatch)
  totalContentFetched += videos.length

  logger.info(
    `Registered ${isInitialBatch ? 'initial' : 'more-news'} batch with ${videos.length} videos for analytics (total: ${totalContentFetched})`
  )
}

/**
 * Map a Game to the analytics payload format.
 * @param game - The Game from the API response
 * @param index - The index within the current batch (used for sortOrder within batch)
 * @param globalOffset - Offset to add for global sortOrder (total content before this batch)
 */
export function mapGameToPayload(
  game: Game,
  index: number,
  globalOffset: number = 0
): FetchedContentPayload {
  return {
    id: game.title,
    sortOrder: globalOffset + index,
    headline: game.title,
    url: game.game_link || undefined,
    category: 'Games',
    type: 'game',
  }
}

/**
 * Register games from an API response as a batch.
 * Games are always treated as an initial batch since they are loaded all at once.
 * @param games - Array of Game from the API response
 */
export function registerGameBatch(games: Game[]): void {
  if (!games || games.length === 0) {
    logger.warn('registerGameBatch called with empty games array')
    return
  }

  const globalOffset = totalContentFetched
  const mappedGames = games.map((game, index) =>
    mapGameToPayload(game, index, globalOffset)
  )

  addToBatch(mappedGames, true)
  totalContentFetched += games.length

  logger.info(
    `Registered initial batch with ${games.length} games for analytics (total: ${totalContentFetched})`
  )
}

/**
 * Get content from the initial page load batch.
 * Used for NtpPage_Viewed event.
 */
export function getInitialBatchContent(): FetchedContentPayload[] {
  if (!initialBatch) {
    logger.warn('getInitialBatchContent called but no initial batch registered')
    return []
  }
  return initialBatch.items
}

/**
 * Get content from the most recent "more news" batch.
 * Used for NtpMoreNews_Loaded event.
 */
export function getLatestMoreNewsBatchContent(): FetchedContentPayload[] {
  if (!latestMoreNewsBatch) {
    logger.warn(
      'getLatestMoreNewsBatchContent called but no more-news batch registered'
    )
    return []
  }
  return latestMoreNewsBatch.items
}

/**
 * Check if an initial batch has been registered.
 */
export function hasInitialBatch(): boolean {
  return initialBatch !== null && initialBatch.items.length > 0
}

/**
 * Check if a more-news batch has been registered.
 */
export function hasMoreNewsBatch(): boolean {
  return latestMoreNewsBatch !== null && latestMoreNewsBatch.items.length > 0
}

/**
 * Clear the more-news batch after the event has been sent.
 * This prevents the same batch from being sent multiple times.
 */
export function clearMoreNewsBatch(): void {
  latestMoreNewsBatch = null
}

/**
 * Clear all batches and reset state.
 * Useful for testing or full page reloads.
 */
export function clearAllBatches(): void {
  initialBatch = null
  latestMoreNewsBatch = null
  totalContentFetched = 0
  sortOrderIndex.clear()
  pageviewSeed = null
  initialBatchAvailable.value = false
}

/**
 * Get the total number of content items (articles + videos) fetched across all batches.
 */
export function getTotalContentFetched(): number {
  return totalContentFetched
}

/**
 * Look up an article/video's sort order by its ID.
 * Uses a persistent index that retains sort orders across ALL batches,
 * including middle more-news batches that have been replaced in latestMoreNewsBatch.
 *
 * This is used by ArticleCard click events to get a sort order consistent with
 * the visibleContent array sent in NtpPage_Viewed and NtpMoreNews_Loaded events.
 *
 * @param id - The content item ID to look up
 * @returns The sort order if found, or undefined if the item isn't in any batch
 *          (e.g., Quartz/Inventory articles tracked via testSectionContent)
 */
export function getSortOrderById(id: string): number | undefined {
  if (!id) return undefined
  return sortOrderIndex.get(id)
}
